import type { Lang } from "../../hero/mock";

// Illustrative mock data for the «Qanday ishlaydi» phone (handoff how-screens.html) — not real customers.
// Data only: no React, no runtime date, locale or random APIs, so SSG and hydration produce identical output.
// UI words come from i18n `how.app.*` / `hero.mock.*` / `hero.stack.*`; only names, products and
// short dates that differ per language are localized here.

export type { Lang };
export type Loc = string | Record<Lang, string>;

const NB = " ";
/** "1 000" → "$1<NBSP>000" (same shape as `usd()` in hero/mock.ts). */
const usd = (n: string) => `$${n.replace(/ /g, NB)}`;

// ---- Home ----------------------------------------------------------------------------------

export const HOME = {
  shop: "Aziz Do‘kon",
  initials: "AD",
  /** Hero amount on the green card. */
  amount: usd("4 100"),
} as const;

/** Monthly collected series behind the sparkline (illustrative). */
export const SPARK_SERIES: readonly number[] = [2300, 2400, 3100, 2800, 3600, 4100];

export interface Sparkline {
  width: number;
  height: number;
  /** Line path (`M…L…`), 1-decimal coordinates. */
  line: string;
  /** Closed area path under the line (for the gradient fill). */
  area: string;
  /** Last point — where the end dot pops. */
  end: { x: number; y: number };
}

// Same algorithm as the ds `Sparkline` (ds-components.jsx): pad 5, min/max normalized, toFixed(1).
function buildSparkline(series: readonly number[], width: number, height: number): Sparkline {
  const pad = 5;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const rg = max - min || 1;
  const pts = series.map((v, i): [number, number] => [
    pad + (width - pad * 2) * (i / (series.length - 1)),
    pad + (height - pad * 2) * (1 - (v - min) / rg),
  ]);
  const line = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  const last = pts[pts.length - 1];
  const area = `${line} L${last[0].toFixed(1)} ${height} L${pad} ${height} Z`;
  return { width, height, line, area, end: { x: last[0], y: last[1] } };
}

export const SPARK: Sparkline = buildSparkline(SPARK_SERIES, 110, 44);

/** in = payment arrived, over = overdue, up = upcoming. */
export type PendingKind = "in" | "over" | "up";

export interface PendingRow {
  name: string;
  product: Loc;
  /** Short date, a time ("14:20") or "yesterday" → i18n `hero.stack.yesterday`. */
  when: Loc;
  amount: string;
  kind: PendingKind;
  /** Days late (over) / days until due (up). */
  days?: number;
}

export const PENDING: readonly PendingRow[] = [
  { name: "Sardor Wizardov", product: "Chevrolet Cobalt", when: "14:20", amount: usd("1 000"), kind: "in" },
  { name: "Malika Rahimova", product: "iPhone 15 Pro", when: { uz: "27 sen", ru: "27 сен" }, amount: usd("150"), kind: "over", days: 6 },
  { name: "Otabek Yuldashev", product: "Samsung Galaxy S25", when: { uz: "5 okt", ru: "5 окт" }, amount: usd("120"), kind: "up", days: 2 },
  { name: "Dilnoza Karimova", product: { uz: "Naqd qarz", ru: "Заём наличными" }, when: "yesterday", amount: usd("250"), kind: "in" },
];

export interface RecentRow {
  name: string;
  time: string;
  method: Loc;
  amount: string;
}

const CASH: Loc = { uz: "Naqd", ru: "Наличные" };
const CARD: Loc = { uz: "Karta", ru: "Карта" };

export const RECENT: readonly RecentRow[] = [
  { name: "Shahzod Mirzayev", time: "09:12", method: CASH, amount: usd("300") },
  { name: "Kamola Ibragimova", time: "08:40", method: CARD, amount: usd("450") },
  { name: "Nilufar Hasanova", time: "18:05", method: CARD, amount: usd("180") },
];

// ---- Create agreement ----------------------------------------------------------------------

export const FIN = {
  price: 9600,
  /** Term chips (months). */
  terms: [3, 6, 12, 24] as readonly number[],
  termFrom: 6,
  termTo: 12,
  firstDate: "03.11.2026",
  /** Keystroke-by-keystroke price typing frames. */
  typed: ["9", "96", "960", `9${NB}600`] as readonly string[],
  /** Monthly payment per term. */
  monthly: { 6: usd("1 600"), 12: usd("800") } as Readonly<Record<number, string>>,
} as const;

/** Normalized 0..1 pen point; scaled to the pad size in the sign component. */
export type StrokePoint = readonly [number, number];

const r4 = (n: number) => Math.round(n * 10000) / 10000;

// Handoff formulas (how-screens.html): a looped signature scrawl plus an underline.
const STROKE_1: StrokePoint[] = Array.from({ length: 70 }, (_, i): StrokePoint => {
  const t = i / 69;
  return [
    r4(0.1 + 0.78 * t + 0.035 * Math.sin(t * Math.PI * 14)),
    r4(0.52 - 0.22 * Math.sin(t * Math.PI * 5) * (0.55 + 0.45 * Math.sin(t * Math.PI)) + 0.07 * Math.cos(t * Math.PI * 14)),
  ];
});
const STROKE_2: StrokePoint[] = Array.from({ length: 18 }, (_, i): StrokePoint => {
  const t = i / 17;
  return [r4(0.16 + 0.66 * t), r4(0.8 - 0.05 * t)];
});

export const SIGN = {
  sub: "Kamola Ibragimova · #NS-1048",
  STROKES: [STROKE_1, STROKE_2] as readonly (readonly StrokePoint[])[],
} as const;

// ---- Agreement detail ----------------------------------------------------------------------

export type ScheduleStatus = "paid" | "late" | "due";

export interface ScheduleRow {
  /** Payment number (1-based). */
  n: number;
  date: Loc;
  status: ScheduleStatus;
}

export const DETAIL = {
  id: "NS-1042",
  client: "Malika Rahimova",
  item: "iPhone 15 Pro",
  type: "technology",
  total: usd("1 800"),
  paid: usd("600"),
  remaining: usd("1 200"),
  /** Paid share of the total (progress bar). */
  ratio: 1 / 3,
  /** [paid months, total months] */
  months: [4, 12] as readonly [number, number],
  monthly: usd("150"),
  late: 6,
  next: { uz: "27 sen", ru: "27 сен" } as Loc,
  start: "27.04.2026",
  phone: "+998 90 412 18 07",
  schedule: [
    { n: 3, date: { uz: "27 iyl", ru: "27 июл" }, status: "paid" },
    { n: 4, date: { uz: "27 avg", ru: "27 авг" }, status: "paid" },
    { n: 5, date: { uz: "27 sen", ru: "27 сен" }, status: "late" },
    { n: 6, date: { uz: "27 okt", ru: "27 окт" }, status: "due" },
    { n: 7, date: { uz: "27 noy", ru: "27 ноя" }, status: "due" },
    { n: 8, date: { uz: "27 dek", ru: "27 дек" }, status: "due" },
  ] as readonly ScheduleRow[],
  /** Rows not shown ("+ yana N ta to‘lov"). */
  more: 4,
} as const;
